import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { observer, inject } from 'mobx-react';
import classNames from 'classnames';
import Icon from '@xo-union/tk-component-icons';
import GuestCandidateModel from '~/glm/models/shared/guest-import/guest-candidate-model';
import GuestCandidateCollection from '~/glm/models/shared/guest-import/guest-candidate-collection';
import styles from './cell.scss';
import { formatPhone, isInternationalPhone } from '~/glm/utils/strings';

export const isPastedSpreadsheet = string => {
  const tabArr = string.split(/\t/);
  const newLineArr = string.split(/\n/);
  return !(tabArr.length === 1 && newLineArr.length === 1);
};

const errorIcon = () => <Icon size="sm" className={styles.errorIcon} name="warning" />;

class Cell extends Component {
  constructor(props, context) {
    super(props, context);
    this.handleOnChange = this.handleOnChange.bind(this);
  }

  state = { typing: false, focused: false };

  handleOnFocus = () => {
    this.setState({ focused: true });
  };

  handleOnBlur = () => {
    this.setState({ focused: false });
  };

  handleOnChange = event => {
    event.persist();
    const { guestCandidates, rowId, colId, fieldName, disabled, handlePaste } = this.props;
    const { value } = event.target;

    if (disabled) return;

    if (isPastedSpreadsheet(value)) {
      this.setState({ focus: false }, () => {
        event.target.blur();
        let pasted = guestCandidates.insertPastedSpreadsheet(value, rowId, colId);
        handlePaste && handlePaste(pasted);
      });
    } else {
      guestCandidates.updateGuest(rowId, { [fieldName]: value });
    }

    if (this.isEmailField && this.emailValid) this.setError('email', false);
    if (this.isPhoneField && this.phoneValid) this.setError('phone', false);
    this.setState({ typing: true });
  };

  // validates when this cell is updated via pasted spreadseet
  componentWillReceiveProps(nextProps) {
    if (nextProps.guest.isNotEmpty && !this.state.typing) {
      this.setState({ typing: false });
    }
  }

  componentDidUpdate() {
    const { focused } = this.state;
    if (!focused) {
      if (this.isEmailField && this.emailValid) this.setError('email', false);
      if (this.isPhoneField && this.phoneValid) this.setError('phone', false);
    }
  }

  setError(errorType, bool) {
    this.props.guest.setProp(`${errorType}Error`, bool);
  }

  get showError() {
    const { guest } = this.props;
    return guest.emailError || guest.phoneError || (guest.isNotEmpty && !guest.validateField('names'));
  }

  get isNamesField() {
    return this.props.fieldName === 'names';
  }

  get isPhoneField() {
    return this.props.fieldName === 'phone';
  }

  get isEmailField() {
    return this.props.fieldName === 'email';
  }

  get emailValid() {
    return !(this.props.guest.email && !this.props.guest.validateField('email'));
  }

  get phoneValid() {
    return !(this.props.guest.phone && !this.props.guest.validateField('phone'));
  }

  get fieldValid() {
    return !(this.props.guest.isNotEmpty && !this.props.guest.isValid);
  }

  get namesValid() {
    const { guest } = this.props;
    return guest.validateField('names');
  }

  setTrackAndRenderError(field) {
    const { handleTrackValidationError } = this.props;
    this.setError(field, true);
    if (handleTrackValidationError) {
      handleTrackValidationError(field);
    }
    return errorIcon();
  }

  checkFieldErrors() {
    if (this.state.focused) return null;
    if (this.isEmailField && !this.emailValid) {
      return this.setTrackAndRenderError('email');
    } else if (this.isPhoneField && !this.phoneValid) {
      return this.setTrackAndRenderError('phone');
    } else if (this.isNamesField && !this.namesValid) {
      return this.setTrackAndRenderError('names');
    }

    return null;
  }

  setMaxLength = () => {
    let maxLength = '';
    if (this.isPhoneField) {
      maxLength = isInternationalPhone(this.props.guest.phone) ? 16 : 12;
    }
    return maxLength;
  };

  dataForNames = formattedValue => {
    const { fieldName, disabled } = this.props;
    if (fieldName === 'names' && formattedValue !== '' && !disabled) {
      return { 'data-title': formattedValue };
    }
    return {};
  };

  render() {
    const { guest, fieldName, disabled, rowId, colId } = this.props;
    const formattedValue = this.isPhoneField ? formatPhone(guest.phone) : guest[fieldName] || '';
    return (
      <div
        {...this.dataForNames(formattedValue)}
        className={classNames(styles.inputCell, styles[fieldName], {
          [styles.error]: this.showError,
        })}
      >
        {this.checkFieldErrors()}
        <textarea
          id={`r${rowId}_c${colId}`}
          data-field-name={fieldName}
          value={formattedValue}
          onChange={this.handleOnChange}
          onFocus={this.handleOnFocus}
          onBlur={this.handleOnBlur}
          onKeyDown={Cell.onKeyDown}
          disabled={disabled}
          maxLength={this.setMaxLength()}
          className={classNames({
            [styles.disabled]: disabled,
          })}
        />
      </div>
    );
  }
}

Cell.propTypes = {
  rowId: PropTypes.number.isRequired,
  colId: PropTypes.number.isRequired,
  guestCandidates: PropTypes.instanceOf(GuestCandidateCollection).isRequired,
  guest: PropTypes.instanceOf(GuestCandidateModel).isRequired,
  fieldName: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  handlePaste: PropTypes.func,
  handleTrackValidationError: PropTypes.func,
};

Cell.onKeyDown = e => {
  if (e.keyCode === 13) e.preventDefault();
};

export default inject('guestCandidates', 'disabled')(observer(Cell));
